import { Dropdown } from "@carbon/react";
import { Controller, UseFormReturn, useWatch } from "react-hook-form";

import { useCreateError } from "src/errors";
import { useGetOperatorsQuery } from "src/graphql/anti-fraud/queries/rules/OperatorsQuery";
import { useGetOperatorLabel } from "src/hooks/useGetOperatorLabel";
import { useT } from "src/internationalization";
import { mapToApiDataType } from "src/mappers/typedValue.mappers";

import { RuleFormData } from "./useRuleDefinition";

export type RuleOperatorFormFieldProps = {
  index: number;
  id: string;
  methods: UseFormReturn<RuleFormData>;
};

export function RuleOperatorsFormField({ index, id, methods }: RuleOperatorFormFieldProps) {
  const createError = useCreateError();

  const { control, setError } = methods;
  const dataType = useWatch({ control, name: "criteria" })[index].property?.dataType;
  const apiDataType = dataType ? mapToApiDataType(dataType) : undefined;
  const t = useT();

  const getOperatorLabel = useGetOperatorLabel();

  const { data } = useGetOperatorsQuery({
    variables: { dataType: apiDataType ?? "String" },
    skip: !apiDataType,
    onError: () => {
      const error = createError("FETCH-OPERATORS-ERROR", null);
      setError("criteria", { message: error.message });
    },
  });

  return (
    <Controller
      name={`criteria.${index}.operator`}
      control={control}
      render={({ field, fieldState }) => {
        return (
          <Dropdown
            id={`operator-${id}`}
            titleText={t("page.rules.modal.create.form.filters.operator.label")}
            label={t("page.rules.modal.create.form.filters.operator.placeholder")}
            items={
              data.filter((operator) =>
                apiDataType === "Code"
                  ? operator.operatorValueType === "argumentList"
                  : operator.operatorValueType === "argument"
              ) ?? []
            }
            itemToString={(item) => (item ? getOperatorLabel(item.code) : "")}
            selectedItem={data?.find((o) => o.code === field.value?.code) ?? null}
            onChange={({ selectedItem }) => {
              field.onChange({
                code: selectedItem?.code,
                valueType: selectedItem?.operatorValueType,
              });
            }}
            invalid={!!fieldState.error}
            invalidText={fieldState.error?.message}
            warn={false}
            disabled={!data || data.length === 0}
          />
        );
      }}
    />
  );
}
